import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Isvg from 'react-inlinesvg';
import { opportunityPropType, intlMessage } from 'constants/propTypes';
import Button from 'components/Button';
import { DropdownPillMenu } from 'components/Dropdown';
import { TodayTomorrowWeekday } from 'components/IntlHelpers';
import Message from 'components/Message';
import { bem } from 'helpers/class-name';
import icon from 'static/img/Core/recommendation-icon.svg';
import { PFE_ICONS } from 'constants/icons';
import { messages } from '../messages';

const { element } = bem('opportunities');

const LOW_AVAILABILITY = 3;

const DataBlock = ({ className, title, numerator, denominator }) => (
  <div className={element('data-block')}>
    <Message as="div" className={element('data-block-header')} message={title} />
    <Message
      as="div"
      className={element('data-block-value', className)}
      message={messages.split}
      values={{
        numerator,
        denominator,
      }}
    />
  </div>
);

DataBlock.propTypes = {
  title: intlMessage,
  className: PropTypes.string,
  numerator: PropTypes.number,
  denominator: PropTypes.number,
};

const Row = ({
  availabilityDropdownRange,
  isManualStudio,
  isRecurring,
  onAcceptOpportunity,
  opportunity,
  setSpotsToAdd,
  spotsToAdd,
  toggleIsRecurring,
  opportunitySource,
}) => {
  const {
    className,
    recommendation,
    startDateTime,
    teacherName,
    totalCapacity,
    totalClasspassReservations,
    totalClasspassCapacity,
    totalReservations,
    waitingClients,
  } = opportunity;

  // SmartSpot-specific logic
  const isSmartSpot = opportunitySource === 'SMARTSPOT';

  const lowAvailabilityClass = classNames({
    'opportunities__row--danger': totalCapacity - totalReservations <= LOW_AVAILABILITY,
  });

  const lowClasspassAvailabilityClass = classNames({
    'opportunities__row--danger': totalClasspassReservations - totalClasspassCapacity <= LOW_AVAILABILITY,
  });

  return (
    <div className={element('row-wrap')}>
      <div className={element('class-info', 'mb-l lg-mb-0')}>
        <Message format="time" value={startDateTime} />
        <Message
          className="opportunities__day-and-date"
          message={messages.dayAndDate}
          values={{
            date: startDateTime,
            day: <TodayTomorrowWeekday key={startDateTime} date={startDateTime} />,
          }}
        />
        <div>{className}</div>
        <div className="opportunities__teacher-info">{teacherName}</div>
      </div>

      <div className={element('prediction', 'pl-h mb-r lg-mb-0')}>
        {isSmartSpot && waitingClients > 0 && (
          // Show waiting clients message when recommendation is not 1
          <div>
            <div className={element('smartspot-message-header')}>
              <Isvg src={PFE_ICONS.notification} />
              <Message
                as="span"
                message={messages.smartSpotWaitingClients}
                values={{ clients: waitingClients, strong: text => <strong>{text}</strong> }}
              />
            </div>
            <div className={element('smartspot-message-secondary')}>
              <Message message={messages.smartSpotProtectingSubheading} />
            </div>
          </div>
        )}
        {isSmartSpot && waitingClients === 0 && (
          // Show protection message when recommendation is 1
          <div>
            <div className={element('smartspot-message-header')}>
              <Isvg src={PFE_ICONS.smartSpotShield} />
              <Message as="span" message={messages.smartSpotProtecting} />
            </div>
            <div className={element('smartspot-message-secondary')}>
              <Message message={messages.smartSpotMaximize} />
            </div>
          </div>
        )}
        {!isSmartSpot && (
          // Regular opportunities - show earning prediction
          <Message
            as="div"
            message={messages.earningPrediction}
            values={{
              spots: recommendation,
            }}
          />
        )}
      </div>

      <div className={element('data', 'ml-h lg-ml-0 lg-mb-0')}>
        {!isManualStudio && (
          <DataBlock
            className={lowAvailabilityClass}
            title={messages.allBookings}
            numerator={totalReservations}
            denominator={totalCapacity}
          />
        )}
        <DataBlock
          className={lowClasspassAvailabilityClass}
          title={messages.cp}
          numerator={totalClasspassReservations}
          denominator={totalClasspassCapacity}
        />
      </div>

      <div className={element('controls-wrap', 'ml-h lg-ml-0')}>
        <div className={element('button-wrap')}>
          <DropdownPillMenu
            className="mr-r"
            attachment="right"
            items={availabilityDropdownRange.map(i => ({
              key: i,
              label: i,
              icon: i === recommendation ? icon : false,
            }))}
            getText="label"
            getKey="key"
            isSelected={available => available.key === spotsToAdd}
            onSelect={available => setSpotsToAdd(opportunity.scheduleId, available.key)}
            renderAsActive={false}
          />
          <Message
            as={Button}
            className="bt--sm"
            type={Button.TYPES.BUTTON}
            styled="primary-outline"
            onClick={() => onAcceptOpportunity(opportunity, spotsToAdd, isRecurring)}
            message={messages.addBtn}
            values={{ spots: spotsToAdd }}
          />
        </div>
        {!isSmartSpot && (
          <label className={element('recurring-label')}>
            <input type="checkbox" checked={isRecurring} onChange={() => toggleIsRecurring(opportunity.scheduleId)} />
            <Message message={messages.checkboxLabel} />
          </label>
        )}
      </div>
    </div>
  );
};

Row.propTypes = {
  availabilityDropdownRange: PropTypes.arrayOf(PropTypes.number),
  isManualStudio: PropTypes.bool,
  isRecurring: PropTypes.bool,
  onAcceptOpportunity: PropTypes.func,
  opportunity: opportunityPropType.isRequired,
  setSpotsToAdd: PropTypes.func,
  spotsToAdd: PropTypes.number,
  toggleIsRecurring: PropTypes.func,
  opportunitySource: PropTypes.string,
};

export default Row;
